import * as React from 'react'
import { Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from './input'

export interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  showStrength?: boolean
  value?: string
  icon?: React.ElementType
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, showStrength = false, value = '', icon: Icon, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false)

    const hasMinLength = value.length >= 8
    const hasLowercase = /[a-z]/.test(value)
    const hasUppercase = /[A-Z]/.test(value)
    const hasNumber = /\d/.test(value)

    const StrengthIndicator = ({ condition, text }: { condition: boolean; text: string }) => (
      <div className="flex items-center gap-2 text-xs">
        {condition ? (
          <CheckCircle2 className="h-3 w-3 text-green-500" />
        ) : (
          <XCircle className="h-3 w-3 text-red-500" />
        )}
        <span className={condition ? 'text-green-600' : 'text-red-600'}>{text}</span>
      </div>
    )

    return (
      <div className="space-y-2">
        <div className="relative">
          {Icon && (
            <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          )}
          <Input
            type={showPassword ? 'text' : 'password'}
            className={cn('pr-10', { 'pl-10': Icon }, className)}
            ref={ref}
            value={value}
            {...props}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {showStrength && value && (
          <div className="space-y-1">
            <StrengthIndicator condition={hasMinLength} text="At least 8 characters" />
            <StrengthIndicator condition={hasLowercase} text="One lowercase letter" />
            <StrengthIndicator condition={hasUppercase} text="One uppercase letter" />
            <StrengthIndicator condition={hasNumber} text="One number" />
          </div>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'

export { PasswordInput }
